import React from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "@aws-amplify/auth";
import {
  Trophy,
  MonitorPlay,
  Calculator,
  Settings,
  Video,
  Home,
  Mic,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

const LimitedAdminDashboard = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const handleSignOut = async () => {
    try {
      await signOut();
      console.log("Successfully signed out");
      navigate("/auth?state=signIn");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const StatCard = ({ title, value, subtitle, icon }) => (
    <div className={`shadow-xl ${
      isDarkMode 
        ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
        : "bg-base-100 border border-base-200"
    } rounded-lg`}>
      <div className="card-body">
        <div className="flex items-center gap-3">
          {React.cloneElement(icon, {
            className: "w-5 h-5 text-primary",
          })}
          <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>{title}</span>
        </div>
        <div className="mt-3">
          <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-base-content"}`}>{value}</p>
          <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"} mt-1`}>{subtitle}</p>
        </div>
      </div>
    </div>
  );

  const CardContainer = ({ children, title, icon, className = "" }) => (
    <div className={`shadow-xl ${
      isDarkMode 
        ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
        : "bg-base-100 border border-base-200"
    } rounded-lg ${className}`}>
      <div className="card-body">
        <div className="flex items-center gap-3 mb-4">
          {React.cloneElement(icon, {
            className: "w-5 h-5 text-primary",
          })}
          <h3 className={`card-title ${isDarkMode ? "text-white" : ""}`}>{title}</h3>
        </div>
        {children}
      </div>
    </div>
  );

  const ActionButton = ({ children, onClick, className = "", to }) => {
    if (to) {
      return (
        <button
          onClick={() => navigate(to)}
          className={`btn btn-primary btn-outline w-full ${className}`}
        >
          {children}
        </button>
      );
    }
    
    return (
      <button
        onClick={onClick}
        className={`btn btn-primary btn-outline w-full ${className}`}
      >
        {children}
      </button>
    );
  };

  const ToolCard = ({ title, description, icon, to, isNew = false }) => (
    <div 
      onClick={() => navigate(to)}
      className={`cursor-pointer shadow-xl ${
        isDarkMode 
          ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 hover:border-brand-logo-purple/30" 
          : "bg-base-100 border border-base-200 hover:border-primary/30"
      } rounded-lg transition-all duration-200 hover:scale-105`}
    >
      <div className="card-body">
        <div className="flex items-center gap-3 mb-2">
          {React.cloneElement(icon, {
            className: "w-6 h-6 text-primary",
          })}
          <h3 className={`card-title text-lg ${isDarkMode ? "text-white" : ""}`}>{title}</h3>
          {isNew && (
            <span className="badge badge-accent badge-sm">NEW</span>
          )}
        </div>
        <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
          {description}
        </p>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : ""}`}>Judge Dashboard</h1>
        <p className={isDarkMode ? "text-gray-400" : "text-base-content/70"}>
          Access judging tools and competition management
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Active Competitions"
          value="2"
          subtitle="Currently judging"
          icon={<Trophy />}
        />
        <StatCard
          title="Judge Station"
          value="Station 1"
          subtitle="Your assigned station"
          icon={<MonitorPlay />}
        />
        <StatCard
          title="Total Routines"
          value="47"
          subtitle="Judged today"
          icon={<Calculator />}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Judging Tools */}
        <CardContainer
          title="Judging Tools"
          icon={<MonitorPlay />}
          className="lg:col-span-2"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ToolCard
              title="Judge Station"
              description="Access your assigned judging station interface"
              icon={<MonitorPlay />}
              to="/judge-station/1"
            />
            <ToolCard
              title="Tabulator"
              description="View live competition results and scoring"
              icon={<Calculator />}
              to="/tabulator"
            />

            <ToolCard
              title="Competition Portal"
              description="Access competition setup and management"
              icon={<Home />}
              to="/competition/portal"
            />
            <ToolCard
              title="Emcee"
              description="Access emcee tools and announcements"
              icon={<Mic />}
              to="/emcee/current"
              isNew={true}
            />
          </div>
        </CardContainer>

        {/* Competition Management */}
        <CardContainer
          title="Competition Management"
          icon={<Trophy />}
          className="lg:col-span-1"
        >
          <div className="space-y-4">
            <div className={`card ${
              isDarkMode ? "bg-[#252D42]/50 border border-white/10" : "bg-base-200"
            }`}>
              <div className="card-body">
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className={`font-medium text-lg ${isDarkMode ? "text-white" : ""}`}>Solo Division</h3>
                    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                      42 entries / 3 rooms
                    </p>
                  </div>
                  <ActionButton className="w-auto" to="/admin/competition">
                    View Details
                  </ActionButton>
                </div>
              </div>
            </div>
            <div className={`card ${
              isDarkMode ? "bg-[#252D42]/50 border border-white/10" : "bg-base-200"
            }`}>
              <div className="card-body">
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className={`font-medium text-lg ${isDarkMode ? "text-white" : ""}`}>Group Division</h3>
                    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                      114 entries / 2 rooms
                    </p>
                  </div>
                  <ActionButton className="w-auto" to="/admin/competition">
                    View Details
                  </ActionButton>
                </div>
              </div>
            </div>
          </div>
        </CardContainer>

        {/* Quick Settings */}
        <CardContainer title="Quick Settings" icon={<Settings />} className="lg:col-span-1">
          <div className="space-y-3">
            <ActionButton to="/admin/settings">Judge Settings</ActionButton>
            <ActionButton to="/admin/settings/judging-awards">Judging & Awards</ActionButton>
          </div>
        </CardContainer>
      </div>
    </div>
  );
};

export default LimitedAdminDashboard;
